import {
	RiArrowLeftRightLine,
	RiDashboardLine,
	RiDatabase2Line,
	RiEdit2Line,
	RiExchangeLine,
	RiNotification3Line,
	RiRefreshLine,
	RiSettings3Line,
	RiShoppingBag3Line,
	RiShoppingCartLine,
	RiStackLine,
	RiStore2Line,
} from "@remixicon/react";

export interface NavItem {
	to: string;
	icon: typeof RiDashboardLine;
	label: string;
}

export interface NavGroup {
	label: string;
	items: NavItem[];
}

/**
 * Sidebar navigation.
 *
 * Three audiences, and the group set differs for each:
 * - org owners/admins get everything, including "System" and the Stores admin
 * - plain members get Application + Catalogue
 * - STORE users get ONLY the Store group: their job is their own shop's items
 *   and orders, so Catalogue and System are hidden entirely
 *
 * Hiding a group here is decoration, not security. The same boundary is
 * enforced by route-level `beforeLoad` guards and, authoritatively, by the
 * backend (`OrgAdmin` extractor / `StoreScope::resolve`).
 */
export function buildNavGroups(canManageSystem: boolean, isStoreUser = false): NavGroup[] {
	// A store user who is not an admin sees only their store.
	if (isStoreUser && !canManageSystem) {
		return [
			{
				label: "Store",
				items: [
					{ to: "/store/items", icon: RiStore2Line, label: "My Store" },
					{ to: "/store/orders", icon: RiShoppingCartLine, label: "Store Orders" },
				],
			},
			{
				label: "Application",
				items: [
					{
						to: "/notifications",
						icon: RiNotification3Line,
						label: "Notifications",
					},
				],
			},
		];
	}

	const groups: NavGroup[] = [
		{
			label: "Application",
			items: [
				{ to: "/dashboard", icon: RiDashboardLine, label: "Dashboard" },
				{ to: "/orders", icon: RiShoppingCartLine, label: "Orders" },
				{
					to: "/notifications",
					icon: RiNotification3Line,
					label: "Notifications",
				},
			],
		},
		{
			label: "Catalogue",
			items: [
				// Member work: triggering a sync and enriching rows.
				{ to: "/catalogue/import", icon: RiRefreshLine, label: "Prohandel" },
				{ to: "/catalogue/editor", icon: RiEdit2Line, label: "Editor" },
				// Admin-only: Matching rewrites the org-wide Farfetch contract,
				// and the two export pages generate/upload what Farfetch ingests.
				// The backend enforces OrgAdmin on those endpoints and each page
				// has a `beforeLoad` bounce, so showing them to a member would
				// only offer links that 403.
				...(canManageSystem
					? [
							{
								to: "/catalogue/matching",
								icon: RiArrowLeftRightLine,
								label: "Matching",
							},
							{ to: "/catalogue/export", icon: RiShoppingBag3Line, label: "Farfetch" },
							{ to: "/catalogue/stock", icon: RiStackLine, label: "Farfetch Stock" },
						]
					: []),
			],
		},
	];

	// Admins see the store app too (via the store picker), and store users who
	// are also admins keep their full nav.
	if (canManageSystem || isStoreUser) {
		groups.push({
			label: "Store",
			items: [
				{ to: "/store/items", icon: RiStore2Line, label: "My Store" },
				{ to: "/store/orders", icon: RiShoppingCartLine, label: "Store Orders" },
			],
		});
	}

	if (canManageSystem) {
		groups.push({
			label: "System",
			items: [
				{ to: "/integrations/prohandel", icon: RiDatabase2Line, label: "Prohandel" },
				{ to: "/integrations/farfetch", icon: RiExchangeLine, label: "Farfetch" },
				{ to: "/stores", icon: RiStore2Line, label: "Stores" },
				{ to: "/settings", icon: RiSettings3Line, label: "Settings" },
			],
		});
	}

	return groups;
}
